import React from "react";
import { useNavigate } from "react-router-dom";
import { PageHero, Section, Eyebrow } from "@/components/PageParts";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Building2, Home as HomeIcon, Brain, Sofa, HandHeart, Users2, Bed, Clock, Sun, ClipboardCheck, Truck, MoveRight, Activity } from "lucide-react";

const SERVICES = [
  [Building2, "Assisted Living", "Residential communities offering support with daily living."],
  [HomeIcon, "Family Care Homes", "Smaller residential settings with personalized private-pay care."],
  [Brain, "Memory Care", "Specialized residential support for cognitive conditions."],
  [Sofa, "Independent Living", "Community living for active private-pay seniors."],
  [HandHeart, "Private-Duty Home Care", "In-home care hours delivered by participating providers."],
  [Users2, "Personal Care", "Assistance with activities of daily living at home."],
  [Users2, "Companion Care", "Companionship and non-medical support hours."],
  [Bed, "Residential Respite", "Short-term residential stays for caregiver relief."],
  [Clock, "In-Home Respite", "Short-term in-home relief for family caregivers."],
  [Sun, "Adult Day Services", "Daytime programs and openings for private-pay participants."],
  [ClipboardCheck, "Care Management", "Professional care coordination engagements."],
  [MoveRight, "Senior Transition / Move Management", "Support relocating and downsizing."],
  [Truck, "Private Transportation", "Private-pay transportation capacity."],
  [Activity, "Behavioral / Community Support", "Private-pay community support services (compliance-gated)."],
];

export default function Services() {
  const nav = useNavigate();
  return (
    <div>
      <PageHero eyebrow="Services" title="Private-pay care & support services"
        subtitle="The platform supports available private-pay care capacity across many residential and non-residential services — architected for hundreds or thousands of independent providers." />
      <Section className="py-20">
        <Eyebrow>Supported categories</Eyebrow>
        <h2 className="mt-3 max-w-2xl font-heading text-3xl font-semibold tracking-tight text-navy">
          Available private-pay care capacity
        </h2>
        <div className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {SERVICES.map(([Icon, title, desc]) => (
            <Card key={title} className="flex gap-4 p-6 transition-shadow hover:shadow-lg" data-testid={`service-${title}`}>
              <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-sky">
                <Icon className="h-5 w-5 text-clinical" />
              </div>
              <div>
                <h3 className="font-heading text-base font-semibold text-navy">{title}</h3>
                <p className="mt-1 text-sm text-slate-500">{desc}</p>
              </div>
            </Card>
          ))}
        </div>
        <div className="mt-12 flex flex-col items-center gap-4 rounded-xl bg-slate-50 p-10 text-center">
          <h3 className="font-heading text-2xl font-semibold text-navy">Are you a provider?</h3>
          <p className="max-w-xl text-slate-500">List your available care capacity and connect with placement professionals.</p>
          <Button data-testid="services-provider-btn" onClick={() => nav("/providers")} className="bg-navy hover:bg-navy/90">
            Explore the provider network
          </Button>
        </div>
      </Section>
    </div>
  );
}
